// 列表页的公共零件：URL 状态、搜索框、视图 chips、筛选按钮、已选条件、分页、滚动加载、三种形态的页框。
// 发货与异常两个模块共用；与订单模块 OrdersPage 同一套样式。建议由主会话提取到 src/components。
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/Button';
import { Empty } from '@/components/States';
import { legacyHref } from '@/shell/nav';
import s from './ListParts.module.css';

/** 视图、筛选、搜索、分页、选中项都写在 URL 查询参数里（刷新、分享、返回都不丢） */
export function useQueryState() {
  const [sp, setSp] = useSearchParams();
  const get = (k: string) => sp.get(k) || undefined;
  const set = (patch: Record<string, string | undefined>, opts: { push?: boolean } = {}) => {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setSp(next, { replace: !opts.push });
  };
  return { sp, get, set };
}

const SEARCH_VARIANT = { phone: 'sPhone', side: 'sSide', desk: 'sDesk' } as const;

export function SearchBox({ value, placeholder, onSearch, variant }: { value: string; placeholder: string; onSearch: (q: string) => void; variant?: 'phone' | 'side' | 'desk' }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <form
      className={[s.search, variant && s[SEARCH_VARIANT[variant]]].filter(Boolean).join(' ')}
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        onSearch(draft.trim());
      }}
    >
      <Icon name="srch" />
      <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={placeholder} enterKeyHint="search" aria-label={placeholder} />
      {draft && (
        <button type="button" className={s.clearQ} aria-label="清空搜索" onClick={() => { setDraft(''); onSearch(''); }}>
          <Icon name="x" size={14} />
        </button>
      )}
    </form>
  );
}

export type ChipItem = { key: string; label: string; count?: number };

/** 视图 chips：横滑、带计数；选中的自动滚进可见区 */
export function Chips({ items, active, onPick, variant }: { items: ChipItem[]; active: string; onPick: (key: string) => void; variant?: 'side' | 'desk' }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector(`.${s.on}`)?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [active]);
  return (
    <div ref={ref} className={[s.chips, variant && s[variant === 'side' ? 'cSide' : 'cDesk']].filter(Boolean).join(' ')}>
      {items.map((c) => (
        <button key={c.key} className={`${s.chip} ${c.key === active ? s.on : ''}`} aria-pressed={c.key === active} onClick={() => onPick(c.key)}>
          {c.label}
          {c.count != null && c.count >= 0 && <i className="tnum">{c.count}</i>}
        </button>
      ))}
    </div>
  );
}

export function FilterButton({ count, onClick, variant }: { count: number; onClick: () => void; variant?: 'bar' | 'box' }) {
  return (
    <button className={[s.fbtn, variant === 'box' ? s.fbox : '', count ? s.fon : ''].join(' ')} onClick={onClick}>
      <Icon name="filter" />筛选{count > 0 && <span className={s.fbadge}>{count}</span>}
    </button>
  );
}

/** 已选条件回显：直接写值，不写「字段：值」（文档 23 §3.4） */
export function Applied({ items, onRemove, onClear }: { items: { key: string; label: string }[]; onRemove: (key: string) => void; onClear: () => void }) {
  if (!items.length) return null;
  return (
    <div className={s.applied}>
      {items.map((c) => (
        <button key={c.key} className={s.achip} onClick={() => onRemove(c.key)} aria-label={`去掉条件 ${c.label}`}>
          {c.label}<Icon name="x" size={14} />
        </button>
      ))}
      {items.length > 1 && <button className={s.clr} onClick={onClear}>清空</button>}
    </div>
  );
}

export function ListBar({ children }: { children: ReactNode }) {
  return <div className={s.listbar}>{children}</div>;
}

/** 手机 / iPad 卡片列表：滚到底自动加载下一页 */
export function LoadMore({ hasNext, loading, count, onMore }: { hasNext: boolean; loading: boolean; count: number; onMore: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const cb = useRef(onMore);
  cb.current = onMore;
  useEffect(() => {
    const el = ref.current;
    if (!el || !hasNext) return;
    const io = new IntersectionObserver((es) => es[0].isIntersecting && cb.current(), { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [hasNext]);
  return (
    <div ref={ref} className={s.more}>
      {hasNext ? (loading ? '加载中…' : <button onClick={onMore}>加载更多</button>) : count > 0 ? '没有更多了' : ''}
    </div>
  );
}

export function Pager({ page, pageSize, count, total, hasNext, onGo }: { page: number; pageSize: number; count: number; total: number; hasNext: boolean; onGo: (p: number) => void }) {
  const from = count ? (page - 1) * pageSize + 1 : 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <footer className={s.pager}>
      <span className="tnum">第 {from}–{from + count - (count ? 1 : 0)} 条 / 共 {total} 条</span>
      <span className={s.sp} />
      <Button size="sm" disabled={page <= 1} disabledReason="已是第一页" onClick={() => onGo(page - 1)}>上一页</Button>
      <span className="tnum">{page} / {pages}</span>
      <Button size="sm" disabled={!hasNext} disabledReason="已是最后一页" onClick={() => onGo(page + 1)}>下一页</Button>
    </footer>
  );
}

/** 没有模块读权限：不空白，写清原因和去处 */
export function NoAccess({ what, legacy }: { what: string; legacy: string }) {
  return (
    <div className={s.center}>
      <Empty icon="lock" title={`当前账号没有「${what}」的查看权限`} action={<a className={s.legacyBtn} href={legacyHref(legacy)}>去老版看看</a>}>
        权限由管理员在「系统设置 · 角色」里分配；需要的话请联系管理员。
      </Empty>
    </div>
  );
}

/* ------------------------------ 三种形态的页框 ------------------------------ */

/** 手机：根页大标题 + 右侧图标按钮，下面是常驻的搜索 / chips，再下面是滚动的卡片区 */
export function PhoneFrame({ title, actions, header, top, children }: { title: string; actions?: ReactNode; header?: ReactNode; top?: ReactNode; children: ReactNode }) {
  return (
    <div className={s.phone}>
      <header className={s.pnb}>
        {header || <><h1>{title}</h1>{actions}</>}
      </header>
      {top && <div className={s.ptop}>{top}</div>}
      <div className={s.pbody}>{children}</div>
    </div>
  );
}

/** iPad：列表栏 360（768–1023 收到 300）+ 13px 分隔条热区 + 详情 */
export function SplitFrame({ title, count, tools, top, list, detail }: { title: string; count?: number; tools?: ReactNode; top?: ReactNode; list: ReactNode; detail: ReactNode }) {
  return (
    <div className={s.split}>
      <section className={s.listCol}>
        <header className={s.lh}>
          <h1>{title}{count != null && <small className="tnum">{count}</small>}</h1>
          {tools}
        </header>
        {top}
        <div className={s.lbody}>{list}</div>
      </section>
      <div className={s.divider} />
      <section className={s.detailCol}>{detail}</section>
    </div>
  );
}

/** 桌面 / iPad 表格：页头 + 视图条 + 表格 + 分页 */
export function DeskFrame({ title, sub, titleAction, tools, top, children, footer, sidebar }: { title: string; sub?: ReactNode; titleAction?: ReactNode; tools?: ReactNode; top?: ReactNode; children: ReactNode; footer?: ReactNode; sidebar?: ReactNode }) {
  return (
    <div className={`${s.desk} ${sidebar ? s.deskWithSidebar : ''}`}>
      <header className={s.dh}>
        {titleAction ? <div className={s.dtitle}><h1>{title}{sub != null && <small className="tnum">{sub}</small>}</h1>{titleAction}</div> : <h1>{title}{sub != null && <small className="tnum">{sub}</small>}</h1>}
        <span className={s.sp} />
        {tools}
      </header>
      {sidebar ? <div className={s.deskWork}>{sidebar}<section className={s.deskArea}>{top && <div className={s.dtop}>{top}</div>}<div className={s.dbody}>{children}</div>{footer}</section></div> : <>{top && <div className={s.dtop}>{top}</div>}<div className={s.dbody}>{children}</div>{footer}</>}
    </div>
  );
}
